<template>
    <Tabs v-if="!js_only" secondary v-model="component" :options="COMPONENTS" class="mb-4" />

    <div v-if="component === 'react'">
        <CopyCode :code="react_code" label="React" class="mb-4" :page="`Embed ${name}`" />

        <div class="flex gap-6">
            <ExternalLink href="https://www.npmjs.com/package/@unified-api/react-directory" title="React component" />
            <ExternalLink href="https://github.com/unified-to/unified-react-integrations-directory"
                title="React component source code" />
        </div>
    </div>

    <div v-if="component === 'svelte'">
        <CopyCode :code="svelte_code" label="Svelte" class="mb-4" :page="`Embed ${name}`" />

        <div class="flex gap-6">
            <ExternalLink href="https://www.npmjs.com/package/@unified-api/svelte-directory" title="Svelte component" />
            <ExternalLink href="https://github.com/unified-to/unified-svelte-integrations-directory"
                title="Svelte component source code" />
        </div>
    </div>

    <div v-if="component === 'js'">
        <CopyCode
            :code="`&lt;script src=&quot;${code}&quot;&gt;&lt;/script&gt;\n&lt;div id=&quot;${did}&quot;&gt&lt;/div&gt`"
            label="JavaScript" class="mb-4" :page="`Embed ${name}`" />

        <ExternalLink href="https://docs.unified.to/concepts/embedded-components" title="JS Documentation" />
    </div>

    <div v-if="component === 'vuejs'">
        <CopyCode :code="vue_code" label="VueJS" class="mb-4" :page="`Embed ${name}`" />

        <div class="flex gap-6">
            <ExternalLink href="https://www.npmjs.com/package/@unified-api/vuejs-directory" title="VueJS component" />
            <ExternalLink href="https://github.com/unified-to/unified-vuejs-integrations-directory"
                title="VueJS component source code" />
        </div>
    </div>

    <div v-if="component === 'angular'">
        <CopyCode :code="angular_code" label="Angular" class="mb-4" :page="`Embed ${name}`" />

        <div class="flex gap-6">
            <ExternalLink href="https://www.npmjs.com/package/@unified-api/angular-directory"
                title="Angular component" />
            <ExternalLink href="https://github.com/unified-to/unified-angular-integrations-directory"
                title="Angular component source code" />
        </div>
    </div>

    <div v-if="component === 'API'">
        <CopyCode :code="api_code" label="API" class="mb-4" :page="`Embed ${name}`" />

        <div class="flex gap-6">
            <ExternalLink href="https://docs.unified.to/unified/integration/Authorize_new_connection"
                title="API documentation" />
            <!-- <ExternalLink href="https://docs.unified.to/tutorials/customize-auth-flow"
                title="Tutorial on customizing auth flow with the API" /> -->
        </div>
    </div>
</template>

<script lang="ts">
const COMPONENTS = [
    'react',
    'angular',
    'vuejs',
    'svelte',
    'js',
    'API',
];

export default {
    props: {
        name: String,
        did: { type: String, default: 'unified_widget' },
        js_only: Boolean,
        workspace_id: String,
        dc: String as PropType<'EU' | 'US'>,
        cats: Array as PropType<string[]>,
        no_style: Boolean,
        uid: String,
        state: String,
        link_url: String,
        surl: String,
        furl: String,
        scopes: Array as PropType<string[]>,
        environment: String,
        lang: String,
        show_cats: { type: Boolean, default: undefined },
        show_tabs: { type: Boolean, default: undefined },
    },
    setup() {
        return {
            COMPONENTS,
        };
    },
    data() {
        return {
            code: '',
            vue_code: '',
            angular_code: '',
            api_code: '',
            react_code: '',
            svelte_code: '',
            component: this.js_only ? 'js' : 'react',
        };
    },
    watch: {
        js_only() {
            this.component = this.js_only ? 'js' : 'react';
        },
        workspace_id() {
            this.changed();
        },
        did() {
            this.changed();
        },
        dc() {
            this.changed();
        },
        cats() {
            this.changed();
        },
        no_style() {
            this.changed();
        },
        uid() {
            this.changed();
        },
        state() {
            this.changed();
        },
        link_url() {
            this.changed();
        },
        surl() {
            this.changed();
        },
        furl() {
            this.changed();
        },
        scopes() {
            this.changed();
        },
        environment() {
            this.changed();
        },
        lang() {
            this.changed();
        },
        show_cats() {
            this.changed();
        },
        show_tabs() {
            this.changed();
        },
    },
    methods: {
        changed() {
            const api_url = this.dc === 'EU' ? 'https://api.eu.unified.to' : 'https://api.unified.to';
            this.code = `${api_url}docs/unified.js?`;
            this.vue_code =
                `<` +
                `template>
    <UnifiedDirectory `;

            this.api_code =
                `<` +
                `script>
interface IIntegration {
    type: string; // Identifier for this integration
    name: string; // The integration's name
    logo_url: string; // The URL of the integration's logo
};

// Retrieve all active integrations in our workspace and display them in your UI
const integrations: IIntegration[] = await (await fetch(\'${useRuntimeConfig().public.API_URL}unified/integration/workspace/${this.workspace_id
                }?summary=true&active=true&`;
            this.angular_code = `// TODO: npm install @unified-api/angular-directory
import UnifiedDirectory from '@unified-api/angular-directory';

<UnifiedAngularDirectoryComponent `;
            this.svelte_code = `// TODO: npm install @unified-api-svelte-directory

<script lang="ts">
  import UnifiedDirectory, { type UnifiedDirectoryProps } from '@unified-api/svelte-directory';

  let props: UnifiedDirectoryProps = {
`;

            this.react_code = `// TODO: npm install @unified-api/react-directory
import UnifiedDirectory from '@unified-api/react-directory';

return (
        <UnifiedDirectory `;

            const options: any = {};
            const v_options: any = {};
            const a_options: any = {};
            const a_auth_options: any = {};
            if (this.workspace_id) {
                v_options.workspace_id = options.wid = this.workspace_id;
            }
            if (this.did) {
                options.did = this.did;
            }
            if (this.dc === 'EU') {
                a_options.dc = v_options.dc = options.dc = 'eu';
            }
            if (this.cats?.length) {
                a_options.cat = options.cat = this.cats.join(',');
                v_options[':categories'] = JSON.stringify(this.cats).replace(/"/g, "'");
            }
            if (this.no_style) {
                options.style = false;
                v_options[':nostyle'] = true;
            }
            if (this.uid) {
                a_auth_options.external_xref = v_options.external_xref = options.external_xref = this.uid;
            }
            if (this.state) {
                a_auth_options.state = v_options.state = options.state = this.state;
            }
            if (this.link_url) {
                options.link_url = this.link_url;
            }
            if (this.surl) {
                a_auth_options.success_redirect = v_options.success_redirect = options.surl = this.surl;
            }
            if (this.furl) {
                a_auth_options.failure_redirect = v_options.failure_redirect = options.furl = this.furl;
            }
            if (this.scopes?.length) {
                a_auth_options.scopes = options.scopes = this.scopes.join(',');
                v_options[':scopes'] = JSON.stringify(this.scopes).replace(/"/g, "'");
            }
            if (this.environment && this.environment !== 'Production') {
                a_options.env = v_options.environment = options.env = this.environment;
            }
            if (this.lang) {
                a_auth_options.lang = v_options.lang = options.lang = this.lang;
            }
            if (this.show_cats === false) {
                v_options[':nocategories'] = options.nocategories = true;
            }
            if (this.show_tabs === false) {
                v_options[':notabs'] = options.notabs = true;
            }

            this.code += new URLSearchParams(options).toString();

            this.api_code += new URLSearchParams(a_options).toString();
            this.api_code +=
                `')).json();

// Redirect your end-user to the integration's authorization page
// https://api.unified.to/unified/integration/auth/${this.workspace_id}/\$\{integration.type\}?${new URLSearchParams(a_auth_options).toString()}&redirect=true${a_options.env ? '&env=' + a_options.env : ''
                }
</` + `script>`;
            this.angular_code +=
                Object.keys(v_options)
                    .map((key) => `${key}="${v_options[key]}"`)
                    .join(' ') + `/>`;

            this.vue_code +=
                Object.keys(v_options)
                    .map((key) => `${key}="${v_options[key]}"`)
                    .join(' ') +
                `/>
</` +
                `template>

<` +
                `script lang="ts">
// TODO: npm install @unified-api/vuejs-directory
import UnifiedDirectory from '@unified-api/vuejs-directory';

export default {
    components: { UnifiedDirectory },
};
</` +
                `script>`;

            this.svelte_code += Object.keys(v_options)
                .map((key) => `    ${key} : ${v_options[key] === true || v_options[key][0] === '[' ? v_options[key] : '"' + v_options[key] + '"'}`)
                .join(',\n');

            this.svelte_code += '\n  };\n</' + 'script>\n\n<UnifiedDirectory {...props}/>';

            this.react_code +=
                Object.keys(v_options)
                    .map((key) => `${key}={${v_options[key] === true || v_options[key][0] === '[' ? v_options[key] : '"' + v_options[key] + '"'}}`)
                    .join(' ') +
                `/>
);`;
        },
    },
    mounted() {
        this.changed();
    },
};
</script>